import React, { useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import Text from "~/components-shared/Text";
import { Alert, Break, Button, Icon, InputText, Link, Modal } from "~/components-shared";
import WorkingStyleChartCard from "~/components/WorkingStyleChartCard";
import { useDispatch } from "react-redux";
import VerifyCodeModal from "~/components/VerifyCodeModal";
import PublicWorkingStyleCreateModal from "~/components/PublicWorkingStyleCreateModal";
import api from "~/lib/api";
import { handleError } from "~/lib/helpers";
import workingStylesActionCreators from "~/store/workingStyles/actions";
import { CRUDActionEnum } from "~/types/enums";
import WorkingStyleEmailSignatureModal from "~/components/WorkingStyleEmailSignatureModal";
import ai from "~/assets/i/logos/ai.png";
import Picture from "~/components-shared/Picture";
const Wrapper = styled.aside`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
`;
const Summary = styled.div`
  position: relative;
`;
const StyledPicture = styled(Picture)`
  position: absolute;
  top: 0;
  left: -1.7rem;
`;
const PublicWorkingStyleSideBar = _ref => {
  let {
    workingStyle
  } = _ref;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [verifyCodeOpen, setVerifyCodeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [emailSignatureOpen, setEmailSignatureOpen] = useState(false);
  const [email, setEmail] = useState("");
  const handleInvite = () => {
    dispatch(workingStylesActionCreators.actionChanged(CRUDActionEnum.INVITE, undefined));
  };
  const handleShare = () => {
    dispatch(workingStylesActionCreators.actionChanged(CRUDActionEnum.SHARE, workingStyle.id));
  };
  const handleSignIn = () => {
    setIsLoading(true);
    api.validateWorkingStyleEmail(email).then(() => {
      setVerifyCodeOpen(true);
    }).catch(e => {
      setError(handleError(e));
    }).finally(() => {
      setIsLoading(false);
    });
  };
  const handleCreate = () => {
    setCreateOpen(true);
  };
  const handleEmailSignature = () => {
    setEmailSignatureOpen(true);
  };
  const handleLeaveFeedback = () => {
    dispatch(workingStylesActionCreators.actionChanged(CRUDActionEnum.FEEDBACK, workingStyle.id));
  };
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    fontSize: 4,
    s: {
      mb: 3
    }
  }, t("PublicWorkingStyleSideBar.weLoveFeedback")), /*#__PURE__*/React.createElement(Link, {
    color: "grey",
    underline: true,
    onClick: handleLeaveFeedback,
    s: {
      mb: 6
    },
    fontSize: 3.5
  }, t("PublicWorkingStyleSideBar.howToImprove")), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    fontSize: 4,
    s: {
      mb: 3
    }
  }, t("PublicWorkingStyleSideBar.useYourEmail")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement("form", {
    onSubmit: e => {
      e.preventDefault();
      handleSignIn();
    }
  }, /*#__PURE__*/React.createElement(InputText, {
    value: email,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      s: {
        mr: 2,
        mt: 1
      },
      src: "mail",
      color: colors.text.secondary
    }),
    placeholder: t("PublicWorkingStyleSideBar.typeEmail"),
    onChange: value => setEmail(value),
    elemAfter: /*#__PURE__*/React.createElement(Button, {
      color: "grey",
      loading: isLoading,
      onClick: handleSignIn,
      fontSize: 3,
      s: {
        px: 3,
        py: 1,
        ml: 2
      },
      fullWidth: true,
      disabled: !email,
      rounded: true
    }, t("PublicWorkingStyleSideBar.signIn"))
  })), /*#__PURE__*/React.createElement(Break, {
    s: {
      my: 3
    }
  }, "or"), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleCreate,
    fontSize: 4,
    s: {
      py: 3,
      mb: 5
    },
    fullWidth: true
  }, t("PublicWorkingStyleSideBar.create")), workingStyle.summary && /*#__PURE__*/React.createElement(Summary, null, /*#__PURE__*/React.createElement(StyledPicture, {
    src: ai,
    s: {
      mr: 1
    },
    width: "1.3rem",
    height: "1.3rem"
  }), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, workingStyle.summary)), /*#__PURE__*/React.createElement(WorkingStyleChartCard, {
    email: workingStyle.email,
    style: {
      width: "100%"
    }
  }), /*#__PURE__*/React.createElement(Break, {
    s: {
      my: 5
    }
  }, t("common.actions.actions")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleShare,
    maxWidth: "230px",
    s: {
      mb: 4
    },
    fullWidth: true
  }, t("PublicWorkingStyleSideBar.shareProfile")), /*#__PURE__*/React.createElement(Button, {
    color: "primaryGrey",
    onClick: handleEmailSignature,
    maxWidth: "230px",
    s: {
      mb: 3
    },
    fullWidth: true
  }, t("PublicWorkingStyleSideBar.emailSignature")), /*#__PURE__*/React.createElement(Link, {
    color: colors.text.secondary,
    onClick: handleInvite,
    underline: true
  }, t("PublicWorkingStyleSideBar.inviteOthers")), /*#__PURE__*/React.createElement(Modal, {
    open: verifyCodeOpen
  }, /*#__PURE__*/React.createElement(VerifyCodeModal, {
    firstName: workingStyle.firstName,
    email: email,
    onClose: () => setVerifyCodeOpen(false)
  })), /*#__PURE__*/React.createElement(Modal, {
    open: createOpen
  }, /*#__PURE__*/React.createElement(PublicWorkingStyleCreateModal, {
    onClose: () => setCreateOpen(false)
  })), /*#__PURE__*/React.createElement(Modal, {
    open: emailSignatureOpen
  }, /*#__PURE__*/React.createElement(WorkingStyleEmailSignatureModal, {
    workingStyle: workingStyle,
    onClose: () => setEmailSignatureOpen(false)
  })));
};
export default PublicWorkingStyleSideBar;